// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
export interface AccountConfig {
  enabled: boolean;
  account_enabled: boolean;
  platform_origin?: string;
}

export interface AccountOptions {
  billing_mode: 'unified' | 'legacy';
  credit_scale: string;
  groups: Record<string, string>;
  group_ratios: Record<string, number | string>;
  models: string[];
  group_models: Record<string, string[]>;
  default_use_auto_group: boolean;
  quota_per_unit: number;
  quota_display_type: string;
  usd_exchange_rate: number;
  custom_currency_symbol: string;
  custom_currency_exchange_rate: number;
}

export interface AccountKey {
  id: number;
  name: string;
  mask: string;
  status: number;
  expired_time: number;
  group: string;
  unlimited_quota: boolean;
  remain_quota: number;
  used_quota?: number;
  created_time?: number;
  accessed_time?: number;
  allow_ips?: string | null;
  cross_group_retry?: boolean;
  model_limits_enabled: boolean;
  model_limits: string;
  credit_budget?: {
    unlimited: boolean;
    available_units: string;
    reserved_units: string;
    consumed_units: string;
    total_limit_units?: string;
    owner_state: string;
  };
}

export interface KeyPage {
  billing_mode: 'unified' | 'legacy';
  items: AccountKey[];
  page: number;
  size: number;
  total: number;
  default_token_id: number;
}

export interface AccountBalance {
  billing_mode: 'unified' | 'legacy';
  fetched_at: string;
  credits?: {
    scale: string;
    availableUnits: string;
    processingUnits: string;
    scheduledUnits: string;
    nearestExpiry: string | null;
    nearestExpiryUnits: string;
    ownershipState: string;
    ledgerState: string;
  };
  quota?: number;
  quota_per_unit?: number;
  quota_display_type?: string;
  usd_exchange_rate?: number;
  custom_currency_symbol?: string;
  custom_currency_exchange_rate?: number;
}

export class AccountError extends Error {
  constructor(public readonly code: string) {
    super(code);
  }
}

/** Match the main platform's display currency when converting a legacy Key limit. */
export function legacyQuota(value: string, options: AccountOptions): number {
  const amount = Number(value);
  const ratio =
    options.quota_display_type === 'CNY'
      ? options.usd_exchange_rate
      : options.quota_display_type === 'CUSTOM'
        ? options.custom_currency_exchange_rate
        : 1;
  const quota =
    options.quota_display_type === 'TOKENS'
      ? amount
      : Math.round((amount / (ratio > 0 ? ratio : 1)) * options.quota_per_unit);
  if (
    !value.trim() ||
    !Number.isFinite(amount) ||
    amount <= 0 ||
    !Number.isSafeInteger(quota) ||
    quota <= 0
  ) {
    throw new AccountError('invalid_amount');
  }
  return quota;
}

export function quotaCurrency(options: AccountOptions): string {
  if (options.quota_display_type === 'TOKENS') {
    return 'Token';
  }
  if (options.quota_display_type === 'CNY') {
    return '¥';
  }
  if (options.quota_display_type === 'CUSTOM') {
    return options.custom_currency_symbol;
  }
  return '$';
}

export async function accountRequest<T>(
  path: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const { method = 'GET', body, signal } = options;
  let response: Response;
  try {
    response = await fetch(`/api/tokenlat/main-account${path}`, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      signal,
      headers:
        body === undefined || method === 'GET'
          ? undefined
          : { 'Content-Type': 'application/json' },
      body:
        body === undefined || method === 'GET'
          ? undefined
          : JSON.stringify(body),
    });
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    throw new AccountError(
      method === 'POST' ? 'key_creation_unconfirmed' : 'account_unavailable',
    );
  }
  const data = await response.json().catch(() => null);
  if (!response.ok || data === null) {
    throw new AccountError(
      data?.msg ??
        (method === 'POST'
          ? 'key_creation_unconfirmed'
          : 'account_unavailable'),
    );
  }
  if (path === '/keys/default' && method !== 'GET') {
    window.dispatchEvent(new Event('tl-coze:account-changed'));
    try {
      localStorage.setItem('tl-coze:account-changed', String(Date.now()));
    } catch {
      // Same-tab invalidation remains available with blocked browser storage.
      window.dispatchEvent(new Event('tl-coze:account-changed'));
    }
  }
  return data as T;
}

export async function accountConfig(
  signal?: AbortSignal,
): Promise<AccountConfig> {
  const response = await fetch('/api/auth/config', {
    credentials: 'same-origin',
    cache: 'no-store',
    signal,
  });
  if (!response.ok) {
    throw new AccountError('account_unavailable');
  }
  const config: AccountConfig = await response.json();
  if (!config.account_enabled) {
    throw new AccountError('account_service_disabled');
  }
  // The server supplies an origin, never an arbitrary action URL.
  const origin = new URL(config.platform_origin ?? '');
  if (
    origin.protocol !== 'https:' ||
    origin.username ||
    origin.password ||
    origin.pathname !== '/' ||
    origin.search ||
    origin.hash
  ) {
    throw new AccountError('account_unavailable');
  }
  return { ...config, platform_origin: origin.origin };
}

/** Exact decimal strings: an account can exceed JavaScript's safe integer range. */
export function formatCredits(units: string, scale = '500'): string {
  if (!/^\d+$/.test(units) || !/^[1-9]\d*$/.test(scale)) {
    return '—';
  }
  const value = BigInt(units);
  const divisor = BigInt(scale);
  const whole = value / divisor;
  const fraction = (((value % divisor) * 1000000n) / divisor)
    .toString()
    .padStart(6, '0')
    .replace(/0+$/, '');
  return `${whole}${fraction ? `.${fraction}` : ''}`;
}

export function creditUnits(value: string, scale: string): string {
  if (!/^\d+(\.\d{1,6})?$/.test(value) || !/^[1-9]\d*$/.test(scale)) {
    throw new AccountError('invalid_amount');
  }
  const [whole, fraction = ''] = value.split('.');
  // Older build targets lower exponentiation to Math.pow, which rejects BigInt.
  const denominator = BigInt(`1${'0'.repeat(fraction.length)}`);
  const scaled =
    (BigInt(whole) * denominator + BigInt(fraction || '0')) * BigInt(scale);
  if (
    scaled <= 0n ||
    scaled % denominator !== 0n ||
    scaled / denominator > 9223372036854775807n
  ) {
    throw new AccountError('invalid_amount');
  }
  return (scaled / denominator).toString();
}

export function keyAvailable(key: AccountKey, now = Date.now()): boolean {
  if (key.credit_budget && !/^\d+$/.test(key.credit_budget.available_units)) {
    return false;
  }
  return (
    key.status === 1 &&
    (key.expired_time === -1 || key.expired_time * 1000 > now) &&
    (key.unlimited_quota || key.remain_quota > 0 || !!key.credit_budget) &&
    (!key.credit_budget || key.credit_budget.owner_state === 'active') &&
    (!key.credit_budget ||
      key.credit_budget.unlimited ||
      BigInt(key.credit_budget.available_units) > 0n)
  );
}
